import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref as dbRef, update } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { ShieldCheck, Upload, CheckCircle, AlertCircle, Camera, ArrowLeft } from "lucide-react";

export default function VerifyId() {
  const { currentUser, fetchUserProfile } = useAuth();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) {
      setError("File size must be under 5MB.");
      return;
    }
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  }

  async function handleUpload() {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const url = await uploadImage(file);
      await update(dbRef(db, `users/${currentUser.uid}`), {
        collegeIdUrl: url,
        idVerificationStatus: "pending",
      });
      await fetchUserProfile(currentUser.uid);
      setDone(true);
    } catch (err) {
      setError("Upload failed. Please try again.");
      
    } finally {
      setUploading(false);
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-8 text-center">
          <CheckCircle size={64} className="text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Submitted!</h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            Your college ID has been submitted for verification. We'll review and verify your account within 24 hours.
          </p>
          <div className="bg-blue-50 rounded-xl p-4 mb-6 text-left">
            <p className="text-sm text-blue-700 font-medium mb-1">What happens next?</p>
            <ul className="text-xs text-blue-600 space-y-1">
              <li>• Our team will review your ID</li>
              <li>• You'll get verified badge once approved</li>
              <li>• You can browse & post listings meanwhile</li>
            </ul>
          </div>
          <button
            onClick={() => navigate("/")}
            className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700"
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6">
        <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100 mb-2"><ArrowLeft size={20} className="text-gray-700" /></button>
        <div className="text-center mb-6">
          <ShieldCheck size={48} className="text-blue-600 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-gray-800">Verify Your College ID</h2>
          <p className="text-gray-500 text-sm mt-2">
            Upload a clear photo of your college ID card to get a verified badge
          </p>
        </div>

        <div className="bg-blue-50 rounded-xl p-4 mb-5">
          <p className="text-sm font-medium text-blue-800 mb-2">Instructions:</p>
          <ul className="text-xs text-blue-600 space-y-1">
            <li>• Make sure your name and college are visible</li>
            <li>• Take a clear, well-lit photo</li>
            <li>• Accepted: JPG, PNG (max 5MB)</li>
            <li>• Your ID will only be seen by our admins</li>
          </ul>
        </div>

        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 mb-4 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            {error}
          </div>
        )}

        <div
          onClick={() => document.getElementById("id-upload").click()}
          className="border-2 border-dashed border-blue-300 rounded-xl overflow-hidden cursor-pointer hover:bg-blue-50 transition mb-4"
        >
          {preview ? (
            <div className="relative">
              <img src={preview} alt="ID Preview" className="w-full max-h-48 object-contain p-2" />
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20 opacity-0 hover:opacity-100 transition">
                <div className="bg-white rounded-full p-2">
                  <Camera size={20} className="text-gray-700" />
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-blue-400">
              <Upload size={36} className="mx-auto mb-3" />
              <p className="text-sm font-medium">Tap to upload your college ID</p>
              <p className="text-xs text-gray-400 mt-1">JPG or PNG, up to 5MB</p>
            </div>
          )}
        </div>
        <input id="id-upload" type="file" accept="image/*" onChange={handleFile} className="hidden" />

        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full bg-blue-600 text-white py-3 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed mb-3"
        >
          {uploading ? "Uploading..." : "Submit for Verification"}
        </button>

        <button
          onClick={() => navigate("/")}
          className="w-full text-gray-400 text-sm py-2 hover:text-gray-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
}
